import React, { useEffect, useState } from 'react';
import {
    Card,
    Row,
    Col,
    Statistic,
    Table,
    Progress,
    Tag,
    DatePicker,
    Space,
    Typography,
    Alert,
    Divider,
    Tabs,
    Modal,
    Button,
    Tooltip,
} from 'antd';
import {
    DollarOutlined,
    RiseOutlined,
    FallOutlined,
    PercentageOutlined,
    WarningOutlined,
    CheckCircleOutlined,
    FileTextOutlined,
    ProjectOutlined,
    CalendarOutlined,
} from '@ant-design/icons';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import axios from 'axios';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const FinanceDashboardPage = () => {
    const [loading, setLoading] = useState(false);
    const [planningLoading, setPlanningLoading] = useState(false);
    const [dashboardData, setDashboardData] = useState(null);
    const [planningData, setPlanningData] = useState(null);
    const [activeTab, setActiveTab] = useState('execution');
    const [dateRange, setDateRange] = useState([
        dayjs().startOf('year'),
        dayjs().endOf('year')
    ]);
    
    // 카테고리 상세 팝업 상태
    const [categoryDetailVisible, setCategoryDetailVisible] = useState(false);
    const [categoryDetailData, setCategoryDetailData] = useState(null);
    const [categoryDetailLoading, setCategoryDetailLoading] = useState(false);

    useEffect(() => {
        fetchDashboardData();
        fetchPlanningData();
    }, [dateRange]);

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE}/finance-dashboard`, {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                    start_date: dateRange[0].format('YYYY-MM-DD'),
                    end_date: dateRange[1].format('YYYY-MM-DD')
                }
            });
            setDashboardData(response.data);
        } catch (error) {
            console.error('Dashboard data fetch error:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchPlanningData = async () => {
        setPlanningLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE}/finance-dashboard/planning`, {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                    start_date: dateRange[0].format('YYYY-MM-DD'),
                    end_date: dateRange[1].format('YYYY-MM-DD')
                }
            });
            setPlanningData(response.data);
        } catch (error) {
            console.error('Planning data fetch error:', error);
        } finally {
            setPlanningLoading(false);
        }
    };

    const handleDateChange = (dates) => {
        if (dates) {
            setDateRange(dates);
        }
    };

    const handleTabChange = (key) => {
        setActiveTab(key);
    };

    // 카테고리 상세 데이터 조회
    const fetchCategoryDetails = async (projectId, category) => {
        setCategoryDetailLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_BASE}/finance-dashboard/project/${projectId}/category/${category}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setCategoryDetailData(response.data);
            setCategoryDetailVisible(true);
        } catch (error) {
            console.error('Category details fetch error:', error);
        } finally {
            setCategoryDetailLoading(false);
        }
    };

    // 카테고리 카드 클릭 핸들러
    const handleCategoryClick = (projectId, category) => {
        fetchCategoryDetails(projectId, category);
    };

    // 금액 포맷 함수
    const formatBillion = (value) => {
        return `${(value / 100000000).toFixed(2)}억`;
    };

    const formatTenMillion = (value) => {
        return `${(value / 10000000).toFixed(2)}천만`;
    };

    const formatMillion = (value) => {
        return `${(value / 1000000).toFixed(2)}백만`;
    };

    const formatNumber = (value) => {
        return value.toLocaleString();
    };

    // 차트 색상
    const COLORS = {
        income: '#52c41a',
        expense: '#ff4d4f',
        net: '#1890ff',
        contract: '#722ed1',
        outsourcing: '#722ed1',
        material: '#13c2c2',
        expense_category: '#faad14',
        labor: '#52c41a',
        other: '#eb2f96',
    };

    const PIE_COLORS = ['#722ed1', '#faad14', '#13c2c2', '#eb2f96'];

    // 계획 단계 프로젝트 테이블 컬럼
    const planningProjectColumns = [
        {
            title: '프로젝트',
            dataIndex: 'name',
            key: 'name',
            width: 200,
            ellipsis: true,
            render: (name, record) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{name}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>{record.code}</Text>
                </Space>
            ),
        },
        {
            title: '고객사',
            dataIndex: 'client_name',
            key: 'client_name',
            width: 150,
            ellipsis: true,
        },
        {
            title: '예상계약금액',
            dataIndex: 'contract_amount',
            key: 'contract_amount',
            width: 120,
            align: 'right',
            render: (amount) => (
                <Text strong style={{ color: '#722ed1' }}>{formatTenMillion(amount)}</Text>
            ),
        },
        {
            title: 'PM',
            dataIndex: 'pm_name',
            key: 'pm_name',
            width: 100,
        },
        {
            title: '부서배분',
            dataIndex: 'department_allocations',
            key: 'department_allocations',
            width: 150,
            render: (allocations, record) => {
                if (record.has_allocations && allocations && allocations.length > 0) {
                    return (
                        <Space direction="vertical" size={2}>
                            {allocations.map(alloc => (
                                <div key={alloc.department_id} style={{ fontSize: 11 }}>
                                    <Tag color="blue" size="small">{alloc.department_code}</Tag>
                                    <Text style={{ fontSize: 10 }}>{alloc.allocation_rate}%</Text>
                                </div>
                            ))}
                        </Space>
                    );
                } else {
                    return <Tag color="blue">{record.department_code}</Tag>;
                }
            },
        },
        {
            title: '등록일',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 100,
            render: (date) => dayjs(date).format('MM/DD'),
        },
        {
            title: '진행률',
            dataIndex: 'progress',
            key: 'progress',
            width: 120,
            align: 'center',
            render: (progress) => (
                <Progress
                    percent={progress}
                    size="small"
                    status={progress > 0 ? 'active' : 'normal'}
                />
            ),
        },
    ];

    // 부서별 수주 예상 테이블 컬럼
    const departmentSummaryColumns = [
        {
            title: '부서',
            dataIndex: 'department_name',
            key: 'department_name',
            render: (name, record) => (
                <Space>
                    <Tag color="blue">{record.department_code}</Tag>
                    <Text strong>{name}</Text>
                </Space>
            ),
        },
        {
            title: '프로젝트 수',
            dataIndex: 'project_count',
            key: 'project_count',
            align: 'center',
            render: (count) => (
                <Tag color="green">{count}건</Tag>
            ),
        },
        {
            title: '예상 수주금액',
            dataIndex: 'total_amount',
            key: 'total_amount',
            align: 'right',
            render: (amount) => (
                <Text strong style={{ color: '#722ed1' }}>{formatBillion(amount)}</Text>
            ),
        },
    ];

    if (!dashboardData && activeTab === 'execution') {
        return <Card loading={loading}>데이터를 불러오는 중...</Card>;
    }

    if (!planningData && activeTab === 'planning') {
        return <Card loading={planningLoading}>데이터를 불러오는 중...</Card>;
    }

    const { totalStats, monthlyTrend, expenseByCategory, departmentStats, projectBudgetStatus } = dashboardData || {};

    // 프로젝트 테이블 컬럼
    const projectColumns = [
        {
            title: '프로젝트',
            dataIndex: 'project_name',
            key: 'project_name',
            width: 180,
            ellipsis: true,
            fixed: 'left',
            render: (name, record) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{name}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>{record.project_code}</Text>
                </Space>
            ),
        },
        {
            title: '계약금액',
            dataIndex: 'contract_amount',
            key: 'contract_amount',
            width: 100,
            align: 'right',
            render: (amount) => (
                <Text strong style={{ color: '#722ed1' }}>{formatTenMillion(amount)}</Text>
            ),
        },
        {
            title: '실제수입',
            dataIndex: 'actual_income',
            key: 'actual_income',
            width: 100,
            align: 'right',
            render: (income) => (
                <Text style={{ color: '#52c41a' }}>{formatTenMillion(income)}</Text>
            ),
        },
        {
            title: '예산',
            dataIndex: 'total_budget',
            key: 'total_budget',
            width: 100,
            align: 'right',
            render: (budget) => formatTenMillion(budget),
        },
        {
            title: '실제지출',
            dataIndex: 'actual_expense',
            key: 'actual_expense',
            width: 100,
            align: 'right',
            render: (expense, record) => (
                <Text type={record.is_over_budget ? 'danger' : undefined}>
                    {formatTenMillion(expense)}
                </Text>
            ),
        },
        {
            title: '집행률',
            dataIndex: 'execution_rate',
            key: 'execution_rate',
            width: 200,
            render: (rate, record) => (
                <Space direction="vertical" size={2} style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text style={{ fontSize: 12 }}>{rate.toFixed(1)}%</Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            잔여: {formatTenMillion(record.remaining)}
                        </Text>
                    </div>
                    <Progress
                        percent={parseFloat(rate.toFixed(1))}
                        size="small"
                        showInfo={false}
                        status={rate > 100 ? 'exception' : rate > 80 ? 'normal' : 'active'}
                        strokeColor={rate > 100 ? '#ff4d4f' : rate > 80 ? '#faad14' : '#52c41a'}
                    />
                </Space>
            ),
        },
        {
            title: '상태',
            dataIndex: 'is_over_budget',
            key: 'status',
            width: 90,
            align: 'center',
            fixed: 'right',
            render: (isOver, record) => {
                if (isOver) {
                    return <Tag color="error" icon={<WarningOutlined />}>초과</Tag>;
                }
                if (record.execution_rate > 80) {
                    return <Tag color="warning">주의</Tag>;
                }
                return <Tag color="success" icon={<CheckCircleOutlined />}>정상</Tag>;
            },
        },
    ];

    // 부서 테이블 컬럼
    const departmentColumns = [
        {
            title: '부서',
            dataIndex: 'department_name',
            key: 'department_name',
            width: 140,
            render: (name, record) => (
                <Space>
                    <Tag color="blue">{record.department_code}</Tag>
                    <Text strong>{name}</Text>
                </Space>
            ),
        },
        {
            title: '계약금액',
            dataIndex: 'contract_amount',
            key: 'contract_amount',
            width: 110,
            align: 'right',
            render: (amount) => (
                <Text style={{ color: '#722ed1' }}>{formatBillion(amount)}</Text>
            ),
        },
        {
            title: '실제수입',
            dataIndex: 'revenue',
            key: 'revenue',
            width: 110,
            align: 'right',
            render: (revenue) => (
                <Text style={{ color: '#52c41a' }}>{formatBillion(revenue)}</Text>
            ),
        },
        {
            title: '지출',
            dataIndex: 'expense',
            key: 'expense',
            width: 110,
            align: 'right',
            render: (expense) => (
                <Text style={{ color: '#ff4d4f' }}>{formatBillion(expense)}</Text>
            ),
        },
        {
            title: '순이익',
            dataIndex: 'net_profit',
            key: 'net_profit',
            width: 110,
            align: 'right',
            render: (profit) => (
                <Text strong type={profit >= 0 ? 'success' : 'danger'}>
                    {profit >= 0 ? '+' : ''}{formatBillion(profit)}
                </Text>
            ),
        },
        {
            title: '이익률',
            dataIndex: 'profit_rate',
            key: 'profit_rate',
            width: 90,
            align: 'right',
            render: (rate) => (
                <Text strong type={rate >= 20 ? 'success' : rate >= 10 ? 'warning' : 'danger'}>
                    {rate.toFixed(1)}%
                </Text>
            ),
        },
    ];

    // 계획 단계 대시보드 렌더링
    const renderPlanningDashboard = () => {
        if (!planningData) return null;

        const { summary, department_summary, recent_projects, all_projects } = planningData;

        return (
            <div>
                {/* 수주 예상 KPI 카드 */}
                <Row gutter={16} style={{ marginBottom: 24 }}>
                    <Col span={8}>
                        <Card>
                            <Statistic
                                title="총 수주 예상 프로젝트"
                                value={summary.total_projects}
                                prefix={<ProjectOutlined style={{ color: '#1890ff' }} />}
                                suffix="건"
                                valueStyle={{ color: '#1890ff', fontSize: 20 }}
                            />
                        </Card>
                    </Col>
                    <Col span={8}>
                        <Card>
                            <Statistic
                                title="총 수주 예상 금액"
                                value={summary.total_amount}
                                prefix={<DollarOutlined style={{ color: '#722ed1' }} />}
                                valueStyle={{ color: '#722ed1', fontSize: 20 }}
                                formatter={(value) => formatBillion(value)}
                            />
                        </Card>
                    </Col>
                    <Col span={8}>
                        <Card>
                            <Statistic
                                title="참여 부서 수"
                                value={summary.department_count}
                                prefix={<CalendarOutlined style={{ color: '#52c41a' }} />}
                                suffix="개 부서"
                                valueStyle={{ color: '#52c41a', fontSize: 20 }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* 부서별 수주 예상 현황 */}
                <Row gutter={16} style={{ marginBottom: 24 }}>
                    <Col span={12}>
                        <Card title="🏢 부서별 수주 예상 현황" size="small">
                            <Table
                                columns={departmentSummaryColumns}
                                dataSource={department_summary}
                                rowKey="department_id"
                                pagination={false}
                                size="small"
                                bordered
                                expandable={{
                                    expandedRowRender: (record) => (
                                        <div style={{ padding: '8px', background: '#f9f9f9' }}>
                                            <Text strong style={{ fontSize: 13 }}>참여 프로젝트:</Text>
                                            <div style={{ marginTop: 8 }}>
                                                {record.projects.map(project => (
                                                    <div key={project.id} style={{ marginBottom: 4, fontSize: 12 }}>
                                                        <Space>
                                                            <Text strong>{project.name}</Text>
                                                            <Tag size="small" color="purple">
                                                                {project.allocation_rate}%
                                                            </Tag>
                                                            <Text type="secondary">
                                                                {formatTenMillion(project.allocated_amount)}
                                                            </Text>
                                                        </Space>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ),
                                }}
                            />
                        </Card>
                    </Col>
                    <Col span={12}>
                        <Card title="📅 최근 등록 프로젝트" size="small">
                            <Table
                                columns={planningProjectColumns.slice(0, 4)}
                                dataSource={recent_projects}
                                rowKey="id"
                                pagination={false}
                                size="small"
                                bordered
                                scroll={{ y: 300 }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* 전체 수주 예상 프로젝트 목록 */}
                <Row gutter={16}>
                    <Col span={24}>
                        <Card title="📋 수주 예상 프로젝트 전체 목록" size="small">
                            <Table
                                columns={planningProjectColumns}
                                dataSource={all_projects}
                                rowKey="id"
                                size="small"
                                bordered
                                pagination={{
                                    pageSize: 15,
                                    showTotal: (total) => `총 ${total}개 프로젝트`,
                                    showSizeChanger: false,
                                }}
                                expandable={{
                                    expandedRowRender: (record) => (
                                        <div style={{ padding: '12px', background: '#fafafa' }}>
                                            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                                <div>
                                                    <Text strong>프로젝트 설명:</Text>
                                                    <br />
                                                    <Text>{record.description || '설명이 없습니다.'}</Text>
                                                </div>
                                                
                                                {/* 부서별 배분 정보 */}
                                                {record.has_allocations && record.department_allocations && record.department_allocations.length > 0 && (
                                                    <div>
                                                        <Text strong>부서별 매출 배분:</Text>
                                                        <div style={{ marginTop: 8 }}>
                                                            <Row gutter={8}>
                                                                {record.department_allocations.map(alloc => (
                                                                    <Col span={6} key={alloc.department_id}>
                                                                        <Card size="small" style={{ textAlign: 'center' }}>
                                                                            <Space direction="vertical" size={4}>
                                                                                <Tag color="blue">{alloc.department_code}</Tag>
                                                                                <Text strong>{alloc.department_name}</Text>
                                                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                                                    {alloc.allocation_rate}%
                                                                                </Text>
                                                                                <Text strong style={{ color: '#722ed1' }}>
                                                                                    {formatTenMillion(alloc.allocated_amount)}
                                                                                </Text>
                                                                            </Space>
                                                                        </Card>
                                                                    </Col>
                                                                ))}
                                                            </Row>
                                                        </div>
                                                    </div>
                                                )}
                                            </Space>
                                        </div>
                                    ),
                                }}
                            />
                        </Card>
                    </Col>
                </Row>
            </div>
        );
    };

    // 기존 재무 집행 대시보드 렌더링
    const renderExecutionDashboard = () => {
        if (!dashboardData) return null;

        return (
            <div>
                {/* KPI 카드 */}
                <Row gutter={16} style={{ marginBottom: 24 }}>
                    <Col span={5}>
                        <Card>
                            <Statistic
                                title="총 계약금액"
                                value={totalStats.total_contract}
                                prefix={<FileTextOutlined style={{ color: '#722ed1' }} />}
                                valueStyle={{ color: '#722ed1', fontSize: 20 }}
                                formatter={(value) => formatBillion(value)}
                            />
                        </Card>
                    </Col>
                    <Col span={5}>
                        <Card>
                            <Statistic
                                title="실제 수입"
                                value={totalStats.total_income}
                                prefix={<RiseOutlined style={{ color: COLORS.income }} />}
                                valueStyle={{ color: COLORS.income, fontSize: 20 }}
                                formatter={(value) => formatBillion(value)}
                            />
                        </Card>
                    </Col>
                    <Col span={5}>
                        <Card>
                            <Statistic
                                title="총 지출"
                                value={totalStats.total_expense}
                                prefix={<FallOutlined style={{ color: COLORS.expense }} />}
                                valueStyle={{ color: COLORS.expense, fontSize: 20 }}
                                formatter={(value) => formatBillion(value)}
                            />
                        </Card>
                    </Col>
                    <Col span={5}>
                        <Card>
                            <Statistic
                                title="순이익"
                                value={totalStats.net_profit}
                                prefix={<DollarOutlined />}
                                valueStyle={{
                                    color: totalStats.net_profit >= 0 ? COLORS.income : COLORS.expense,
                                    fontSize: 20
                                }}
                                formatter={(value) => formatBillion(value)}
                            />
                        </Card>
                    </Col>
                    <Col span={4}>
                        <Card>
                            <Statistic
                                title="이익률"
                                value={totalStats.profit_rate}
                                prefix={<PercentageOutlined />}
                                suffix="%"
                                precision={1}
                                valueStyle={{
                                    color: totalStats.profit_rate >= 20 ? COLORS.income :
                                        totalStats.profit_rate >= 10 ? '#faad14' : COLORS.expense,
                                    fontSize: 20
                                }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* 예산 초과 경고 */}
                {projectBudgetStatus && projectBudgetStatus.some(p => p.is_over_budget) && (
                    <Alert
                        message="⚠️ 예산 초과 프로젝트 발견"
                        description={
                            <div>
                                {projectBudgetStatus.filter(p => p.is_over_budget).map(p => (
                                    <div key={p.project_id} style={{ marginBottom: 4 }}>
                                        <Text strong>{p.project_name}</Text>:
                                        예산 {formatTenMillion(p.total_budget)} /
                                        실제 {formatTenMillion(p.actual_expense)}
                                        <Text type="danger"> (초과: {formatTenMillion(p.actual_expense - p.total_budget)})</Text>
                                    </div>
                                ))}
                            </div>
                        }
                        type="error"
                        showIcon
                        style={{ marginBottom: 24 }}
                    />
                )}

                {/* 차트 섹션 */}
                <Row gutter={16} style={{ marginBottom: 24 }}>
                    {/* 월별 수입/지출 추이 */}
                    <Col span={16}>
                        <Card title="📈 월별 수입/지출 추이" size="small">
                            <ResponsiveContainer width="100%" height={320}>
                                <LineChart data={monthlyTrend}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="month"
                                        style={{ fontSize: 12 }}
                                    />
                                    <YAxis
                                        tickFormatter={(value) => `${(value / 100000000).toFixed(1)}억`}
                                        style={{ fontSize: 12 }}
                                    />
                                    <RechartsTooltip
                                        formatter={(value) => `${formatNumber(value)}원`}
                                        labelStyle={{ fontWeight: 'bold' }}
                                    />
                                    <Legend />
                                    <Line
                                        type="monotone"
                                        dataKey="income"
                                        stroke={COLORS.income}
                                        strokeWidth={2}
                                        name="수입"
                                        dot={{ r: 4 }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="expense"
                                        stroke={COLORS.expense}
                                        strokeWidth={2}
                                        name="지출"
                                        dot={{ r: 4 }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="net"
                                        stroke={COLORS.net}
                                        strokeWidth={2}
                                        name="순이익"
                                        strokeDasharray="5 5"
                                        dot={{ r: 4 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </Card>
                    </Col>

                    {/* 지출 항목별 분포 */}
                    <Col span={8}>
                        <Card title="🥧 지출 항목별 분포" size="small">
                            <ResponsiveContainer width="100%" height={320}>
                                <PieChart>
                                    <Pie
                                        data={expenseByCategory}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={90}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {expenseByCategory && expenseByCategory.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip
                                        formatter={(value) => `${formatNumber(value)}원`}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </Card>
                    </Col>
                </Row>

                {/* 부서별 수익성 차트 */}
                <Row gutter={16} style={{ marginBottom: 24 }}>
                    <Col span={24}>
                        <Card title="🏢 부서별 수익성 차트" size="small">
                            <ResponsiveContainer width="100%" height={320}>
                                <BarChart data={departmentStats}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="department_name"
                                        style={{ fontSize: 12 }}
                                    />
                                    <YAxis
                                        tickFormatter={(value) => `${(value / 100000000).toFixed(1)}억`}
                                        style={{ fontSize: 12 }}
                                    />
                                    <RechartsTooltip
                                        formatter={(value) => `${formatNumber(value)}원`}
                                    />
                                    <Legend />
                                    <Bar dataKey="contract_amount" fill={COLORS.contract} name="계약금액" />
                                    <Bar dataKey="revenue" fill={COLORS.income} name="실제수입" />
                                    <Bar dataKey="expense" fill={COLORS.expense} name="지출" />
                                    <Bar dataKey="net_profit" fill={COLORS.net} name="순이익" />
                                </BarChart>
                            </ResponsiveContainer>
                        </Card>
                    </Col>
                </Row>

                {/* 부서별 상세 테이블 */}
                <Row gutter={16} style={{ marginBottom: 24 }}>
                    <Col span={24}>
                        <Card title="📊 부서별 상세 현황" size="small">
                            <Table
                                columns={departmentColumns}
                                dataSource={departmentStats}
                                rowKey="department_id"
                                pagination={false}
                                size="small"
                                bordered
                            />
                        </Card>
                    </Col>
                </Row>

                {/* 프로젝트별 예산 집행 현황 */}
                <Row gutter={16}>
                    <Col span={24}>
                        <Card title="📋 프로젝트별 예산 집행 현황" size="small">
                            <Table
                                columns={projectColumns}
                                dataSource={projectBudgetStatus}
                                rowKey="project_id"
                                scroll={{ x: 1100 }}
                                size="small"
                                bordered
                                pagination={{
                                    pageSize: 10,
                                    showTotal: (total) => `총 ${total}개 프로젝트`,
                                    showSizeChanger: false,
                                }}
                                expandable={{
                                    expandedRowRender: (record) => (
                                        <div style={{ padding: '16px 24px', background: '#fafafa' }}>
                                            {/* 항목별 예산 집행 상세 */}
                                            <Title level={5} style={{ marginBottom: 16 }}>
                                                📑 항목별 예산 집행 상세
                                            </Title>
                                            <Row gutter={16}>
                                                {Object.entries(record.budget_by_category).map(([category, data]) => {
                                                    const rate = data.budget > 0 ? (data.actual / data.budget * 100) : 0;
                                                    const categoryNames = {
                                                        outsourcing: '외주비',
                                                        material: '자재비',
                                                        expense: '경비',
                                                        other: '기타',
                                                        labor: '내부인건비'
                                                    };
                                                    return (
                                                        <Col flex="1" key={category} style={{ minWidth: '180px' }}>
                                                            {category === 'labor' ? (
                                                                <Tooltip title="내부인건비는 투입인력 현황에서 확인하세요">
                                                                    <Card 
                                                                        size="small" 
                                                                        bordered 
                                                                        style={{ 
                                                                            cursor: 'default',
                                                                            transition: 'all 0.3s ease',
                                                                            opacity: 0.7,
                                                                            backgroundColor: '#f5f5f5'
                                                                        }}
                                                                    >
                                                                        <div style={{ textAlign: 'center', marginBottom: 8 }}>
                                                                            <Text strong style={{ fontSize: 16 }}>
                                                                                {categoryNames[category]}
                                                                            </Text>
                                                                        </div>
                                                                        <Statistic
                                                                            value={rate}
                                                                            suffix="%"
                                                                            precision={1}
                                                                            valueStyle={{
                                                                                fontSize: 24,
                                                                                textAlign: 'center',
                                                                                color: rate > 100 ? '#ff4d4f' : rate > 80 ? '#faad14' : '#52c41a'
                                                                            }}
                                                                            style={{ marginBottom: 12 }}
                                                                        />
                                                                        <Divider style={{ margin: '12px 0' }} />
                                                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                                <Text type="secondary" style={{ fontSize: 12 }}>예산:</Text>
                                                                                <Text strong style={{ fontSize: 12 }}>
                                                                                    {formatMillion(data.budget)}
                                                                                </Text>
                                                                            </div>
                                                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                                <Text type="secondary" style={{ fontSize: 12 }}>실제:</Text>
                                                                                <Text strong style={{ fontSize: 12, color: rate > 100 ? '#ff4d4f' : '#1890ff' }}>
                                                                                    {formatMillion(data.actual)}
                                                                                </Text>
                                                                            </div>
                                                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                                <Text type="secondary" style={{ fontSize: 12 }}>잔여:</Text>
                                                                                <Text strong style={{ fontSize: 12, color: data.budget - data.actual >= 0 ? '#52c41a' : '#ff4d4f' }}>
                                                                                    {formatMillion(data.budget - data.actual)}
                                                                                </Text>
                                                                            </div>
                                                                        </Space>
                                                                        <Progress
                                                                            percent={Math.min(rate, 100)}
                                                                            size="small"
                                                                            showInfo={false}
                                                                            status={rate > 100 ? 'exception' : 'active'}
                                                                            style={{ marginTop: 12 }}
                                                                        />
                                                                    </Card>
                                                                </Tooltip>
                                                            ) : (
                                                                <Card 
                                                                    size="small" 
                                                                    bordered 
                                                                    hoverable
                                                                    style={{ 
                                                                        cursor: 'pointer',
                                                                        transition: 'all 0.3s ease'
                                                                    }}
                                                                    onClick={() => handleCategoryClick(record.project_id, category)}
                                                                >
                                                                    <div style={{ textAlign: 'center', marginBottom: 8 }}>
                                                                        <Text strong style={{ fontSize: 16 }}>
                                                                            {categoryNames[category]}
                                                                        </Text>
                                                                    </div>
                                                                    <Statistic
                                                                        value={rate}
                                                                        suffix="%"
                                                                        precision={1}
                                                                        valueStyle={{
                                                                            fontSize: 24,
                                                                            textAlign: 'center',
                                                                            color: rate > 100 ? '#ff4d4f' : rate > 80 ? '#faad14' : '#52c41a'
                                                                        }}
                                                                        style={{ marginBottom: 12 }}
                                                                    />
                                                                    <Divider style={{ margin: '12px 0' }} />
                                                                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                            <Text type="secondary" style={{ fontSize: 12 }}>예산:</Text>
                                                                            <Text strong style={{ fontSize: 12 }}>
                                                                                {formatMillion(data.budget)}
                                                                            </Text>
                                                                        </div>
                                                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                            <Text type="secondary" style={{ fontSize: 12 }}>실제:</Text>
                                                                            <Text strong style={{ fontSize: 12, color: rate > 100 ? '#ff4d4f' : '#1890ff' }}>
                                                                                {formatMillion(data.actual)}
                                                                            </Text>
                                                                        </div>
                                                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                            <Text type="secondary" style={{ fontSize: 12 }}>잔여:</Text>
                                                                            <Text strong style={{ fontSize: 12, color: data.budget - data.actual >= 0 ? '#52c41a' : '#ff4d4f' }}>
                                                                                {formatMillion(data.budget - data.actual)}
                                                                            </Text>
                                                                        </div>
                                                                    </Space>
                                                                    <Progress
                                                                        percent={Math.min(rate, 100)}
                                                                        size="small"
                                                                        showInfo={false}
                                                                        status={rate > 100 ? 'exception' : 'active'}
                                                                        style={{ marginTop: 12 }}
                                                                    />
                                                                </Card>
                                                            )}
                                                        </Col>
                                                    );
                                                })}
                                            </Row>
                                        </div>
                                    ),
                                }}
                            />
                        </Card>
                    </Col>
                </Row>
            </div>
        );
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <Title level={2}>💰 재무 대시보드</Title>
                <RangePicker
                    value={dateRange}
                    onChange={handleDateChange}
                    format="YYYY-MM-DD"
                    allowClear={false}
                />
            </div>

            <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                size="large"
                items={[
                    {
                        key: 'execution',
                        label: (
                            <span>
                                <DollarOutlined />
                                재무 집행 현황
                            </span>
                        ),
                        children: renderExecutionDashboard()
                    },
                    {
                        key: 'planning',
                        label: (
                            <span>
                                <ProjectOutlined />
                                수주 예상 현황
                            </span>
                        ),
                        children: renderPlanningDashboard()
                    }
                ]}
            />

            {/* 카테고리 상세 팝업 */}
            <Modal
                title={
                    categoryDetailData ? (
                        <Space>
                            <Text strong style={{ fontSize: 16 }}>
                                📊 {categoryDetailData.project_info.name}
                            </Text>
                            <Tag color="blue">{categoryDetailData.category.name}</Tag>
                        </Space>
                    ) : '카테고리 상세'
                }
                open={categoryDetailVisible}
                onCancel={() => {
                    setCategoryDetailVisible(false);
                    setCategoryDetailData(null);
                }}
                footer={[
                    <Button key="close" onClick={() => setCategoryDetailVisible(false)}>
                        닫기
                    </Button>
                ]}
                width={1000}
                style={{ top: 20 }}
            >
                {categoryDetailLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        로딩 중...
                    </div>
                ) : categoryDetailData ? (
                    <div>
                        {/* 프로젝트 기본 정보 */}
                        <Card size="small" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={6}>
                                    <Statistic
                                        title="프로젝트 코드"
                                        value={categoryDetailData.project_info.code}
                                        valueStyle={{ fontSize: 14 }}
                                    />
                                </Col>
                                <Col span={6}>
                                    <Statistic
                                        title="클라이언트"
                                        value={categoryDetailData.project_info.client_name || ''}
                                        valueStyle={{ fontSize: 14 }}
                                    />
                                </Col>
                                <Col span={6}>
                                    <Statistic
                                        title="프로젝트 매출액"
                                        value={categoryDetailData.project_info.contract_amount}
                                        formatter={(value) => `${formatNumber(Math.round(value))}원`}
                                        valueStyle={{ fontSize: 14, color: '#1890ff' }}
                                    />
                                </Col>
                                <Col span={6}>
                                    <Statistic
                                        title="집행률"
                                        value={categoryDetailData.summary.execution_rate}
                                        suffix="%"
                                        precision={1}
                                        valueStyle={{ 
                                            fontSize: 14, 
                                            color: categoryDetailData.summary.execution_rate > 100 ? '#ff4d4f' : '#52c41a' 
                                        }}
                                    />
                                </Col>
                            </Row>
                        </Card>

                        {/* 카테고리 요약 */}
                        <Card size="small" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Statistic
                                        title="총 예산"
                                        value={categoryDetailData.summary.total_budget}
                                        formatter={(value) => `${formatNumber(Math.round(value))}원`}
                                        valueStyle={{ color: '#1890ff' }}
                                    />
                                </Col>
                                <Col span={8}>
                                    <Statistic
                                        title="총 실제"
                                        value={categoryDetailData.summary.total_actual}
                                        formatter={(value) => `${formatNumber(Math.round(value))}원`}
                                        valueStyle={{ color: '#52c41a' }}
                                    />
                                </Col>
                                <Col span={8}>
                                    <Statistic
                                        title="거래건수/항목수"
                                        value={`${categoryDetailData.summary.total_transaction_count}/${categoryDetailData.summary.total_items}`}
                                        suffix="건/항목"
                                        valueStyle={{ color: '#722ed1' }}
                                    />
                                </Col>
                            </Row>
                        </Card>

                        {/* 내부인건비의 경우 */}
                        {categoryDetailData.category.key === 'labor' ? (
                            <Card title="👥 프로젝트 투입 인력 현황" size="small">
                                <Table
                                    dataSource={categoryDetailData.budget_items}
                                    rowKey="id"
                                    size="small"
                                    pagination={{ pageSize: 10 }}
                                    columns={[
                                        {
                                            title: '구분',
                                            dataIndex: 'vendor_name',
                                            key: 'vendor_name',
                                            width: 120,
                                        },
                                        {
                                            title: '인력 정보',
                                            dataIndex: 'description',
                                            key: 'description',
                                            ellipsis: true
                                        },
                                        {
                                            title: '예산금액',
                                            dataIndex: 'budget_amount',
                                            key: 'budget_amount',
                                            width: 120,
                                            align: 'right',
                                            render: (amount) => (
                                                <Text strong style={{ color: '#1890ff' }}>
                                                    {formatNumber(Math.round(amount))}원
                                                </Text>
                                            )
                                        },
                                        {
                                            title: '실제금액',
                                            dataIndex: 'actual_amount',
                                            key: 'actual_amount',
                                            width: 120,
                                            align: 'right',
                                            render: (amount) => (
                                                <Text style={{ color: '#52c41a' }}>
                                                    {formatNumber(Math.round(amount))}원
                                                </Text>
                                            )
                                        },
                                        {
                                            title: '진행률',
                                            dataIndex: 'execution_rate',
                                            key: 'execution_rate',
                                            width: 100,
                                            align: 'center',
                                            render: (rate) => (
                                                <Progress 
                                                    percent={Math.round(rate)} 
                                                    size="small" 
                                                    status={rate > 100 ? 'exception' : 'normal'}
                                                />
                                            )
                                        },
                                        {
                                            title: '세부사항',
                                            dataIndex: 'notes',
                                            key: 'notes',
                                            ellipsis: true,
                                            render: (notes) => notes || '-'
                                        }
                                    ]}
                                />
                            </Card>
                        ) : (
                            /* 일반 예산항목의 경우 */
                            <Card title="💰 예산 항목 및 거래 내역" size="small">
                                <Table
                                    dataSource={categoryDetailData.budget_items}
                                    rowKey="id"
                                    size="small"
                                    pagination={{ pageSize: 10 }}
                                    expandable={{
                                        expandedRowRender: (record) => {
                                            const allTransactions = [
                                                ...(record.project_transactions || []).map(t => ({ ...t, source: '지출' })),
                                                ...(record.card_transactions || []).map(t => ({ ...t, source: '법인카드' }))
                                            ].sort((a, b) => new Date(b.date) - new Date(a.date));

                                            return (
                                                <div style={{ padding: '16px', background: '#fafafa' }}>
                                                    <Text strong style={{ marginBottom: 16, display: 'block' }}>
                                                        💳 거래내역 ({allTransactions.length}건)
                                                    </Text>
                                                    {allTransactions.length > 0 ? (
                                                        <Table
                                                            dataSource={allTransactions}
                                                            rowKey={(record, index) => `${record.source}_${record.date}_${index}`}
                                                            size="small"
                                                            pagination={false}
                                                            columns={[
                                                                {
                                                                    title: '거래일자',
                                                                    dataIndex: 'date',
                                                                    key: 'date',
                                                                    width: 120,
                                                                    render: (date) => dayjs(date).format('YYYY-MM-DD')
                                                                },
                                                                {
                                                                    title: '구분',
                                                                    dataIndex: 'source',
                                                                    key: 'source',
                                                                    width: 80,
                                                                    render: (source) => (
                                                                        <Tag color={source === '법인카드' ? 'gold' : 'blue'}>
                                                                            {source}
                                                                        </Tag>
                                                                    )
                                                                },
                                                                {
                                                                    title: '내용',
                                                                    dataIndex: 'description',
                                                                    key: 'description',
                                                                    ellipsis: true
                                                                },
                                                                {
                                                                    title: '거래처',
                                                                    dataIndex: 'vendor',
                                                                    key: 'vendor',
                                                                    width: 150,
                                                                    render: (vendor) => vendor || '-'
                                                                },
                                                                {
                                                                    title: '금액',
                                                                    dataIndex: 'amount',
                                                                    key: 'amount',
                                                                    width: 120,
                                                                    align: 'right',
                                                                    render: (amount) => (
                                                                        <Text strong style={{ color: '#ff4d4f' }}>
                                                                            {formatNumber(Math.round(amount))}원
                                                                        </Text>
                                                                    )
                                                                }
                                                            ]}
                                                        />
                                                    ) : (
                                                        <Text type="secondary">거래내역이 없습니다.</Text>
                                                    )}
                                                </div>
                                            );
                                        },
                                        rowExpandable: (record) => (record.project_transactions?.length || 0) + (record.card_transactions?.length || 0) > 0
                                    }}
                                    columns={[
                                        {
                                            title: '구분',
                                            dataIndex: 'vendor_name',
                                            key: 'vendor_name',
                                            width: 120,
                                        },
                                        {
                                            title: '내용',
                                            dataIndex: 'description',
                                            key: 'description',
                                            ellipsis: true
                                        },
                                        {
                                            title: '예산금액',
                                            dataIndex: 'budget_amount',
                                            key: 'budget_amount',
                                            width: 120,
                                            align: 'right',
                                            render: (amount) => (
                                                <Text strong style={{ color: '#1890ff' }}>
                                                    {formatNumber(Math.round(amount))}원
                                                </Text>
                                            )
                                        },
                                        {
                                            title: '실제금액',
                                            dataIndex: 'actual_amount',
                                            key: 'actual_amount',
                                            width: 120,
                                            align: 'right',
                                            render: (amount) => (
                                                <Text style={{ color: '#52c41a' }}>
                                                    {formatNumber(Math.round(amount))}원
                                                </Text>
                                            )
                                        },
                                        {
                                            title: '집행률',
                                            dataIndex: 'execution_rate',
                                            key: 'execution_rate',
                                            width: 100,
                                            align: 'center',
                                            render: (rate) => (
                                                <Progress 
                                                    percent={Math.round(rate)} 
                                                    size="small" 
                                                    status={rate > 100 ? 'exception' : 'normal'}
                                                />
                                            )
                                        },
                                        {
                                            title: '거래건수',
                                            key: 'transaction_count',
                                            width: 100,
                                            align: 'center',
                                            render: (record) => {
                                                const count = (record.project_transactions?.length || 0) + (record.card_transactions?.length || 0);
                                                return <Text>{count}건</Text>;
                                            }
                                        },
                                        {
                                            title: '비고',
                                            dataIndex: 'notes',
                                            key: 'notes',
                                            ellipsis: true,
                                            render: (notes) => notes || '-'
                                        }
                                    ]}
                                />
                            </Card>
                        )}
                    </div>
                ) : null}
            </Modal>
        </div>
    );
};

export default FinanceDashboardPage;